(() => {
  if (customElements.get('sn51-reviews')) return;

  class SomnilaReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-sn51-track]');
      if (!this.track) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.cards = Array.from(this.track.children);
      this.controls = Array.from(this.querySelectorAll('[data-sn51-nav]'));
      this.status = this.querySelector('[data-sn51-status]');
      this.dialog = this.querySelector('[data-sn51-dialog]');
      this.dialogContent = this.querySelector('[data-sn51-dialog-content]');
      this.closeButton = this.querySelector('[data-sn51-close]');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.controls.forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => this.move(Number(button.dataset.sn51Nav)), options);
      });
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { ...options, passive: true });
      this.track.addEventListener('keydown', event => this.onTrackKeydown(event), options);
      this.track.addEventListener('click', event => {
        const opener = event.target.closest('[data-sn51-open]');
        if (!opener || !this.track.contains(opener) || typeof this.dialog?.showModal !== 'function') return;
        event.preventDefault();
        this.openReview(opener);
      }, options);
      this.closeButton?.addEventListener('click', () => this.dialog.close(), options);
      this.dialog?.addEventListener('cancel', event => {
        event.preventDefault();
        this.dialog.close();
      }, options);
      this.dialog?.addEventListener('close', () => this.afterClose(), options);
      this.dialog?.addEventListener('keydown', event => this.trapFocus(event), options);
      this.dialog?.addEventListener('pointerdown', event => {
        this.backdropPress = this.isBackdrop(event);
      }, options);
      this.dialog?.addEventListener('click', event => {
        if (this.backdropPress && this.isBackdrop(event)) this.dialog.close();
        this.backdropPress = false;
      }, options);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.scheduleUpdate());
        this.observer.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.scheduleUpdate(), options);
      }
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.sn51-review');
        if (card) this.track.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: 'auto' });
      }, options);
      this.update();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
      clearTimeout(this.announceTimer);
      this.unlockPage();
    }

    get step() {
      const card = this.cards[0];
      return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : 0;
    }

    move(direction) {
      this.track.scrollBy({ left: direction * this.step, behavior: this.reduced.matches ? 'auto' : 'smooth' });
    }

    onTrackKeydown(event) {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: this.reduced.matches ? 'auto' : 'smooth' });
      }
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const left = Math.max(0, this.track.scrollLeft);
      this.controls.forEach(button => {
        button.disabled = Number(button.dataset.sn51Nav) < 0 ? left <= 2 : left >= max - 2;
      });
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        if (!this.status || !this.cards.length) return;
        const current = Math.min(this.cards.length, Math.round(left / Math.max(1, this.step)) + 1);
        const label = (this.dataset.positionLabel || '').replace('[current]', String(current)).replace('[total]', String(this.cards.length));
        if (this.status.textContent !== label) this.status.textContent = label;
      }, 150);
    }

    openReview(opener) {
      const original = opener.closest('[data-sn51-review]').querySelector('[data-sn51-full]');
      if (!original || this.dialog.open) return;
      const content = original.cloneNode(true);
      const heading = content.querySelector('[data-sn51-title]');
      heading.id = this.dialog.getAttribute('aria-labelledby');
      content.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      this.dialogContent.replaceChildren(content);
      this.opener = opener;
      this.dialog.showModal();
      document.documentElement.classList.add('sn51-dialog-open');
      this.closeButton.focus({ preventScroll: true });
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const bounds = this.dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const elements = Array.from(this.dialog.querySelectorAll('button:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])'))
        .filter(element => element.getClientRects().length);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    afterClose() {
      this.unlockPage();
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.dialogContent.replaceChildren();
      this.opener = null;
    }

    unlockPage() {
      if (!document.querySelector('sn51-reviews dialog[open]')) document.documentElement.classList.remove('sn51-dialog-open');
    }
  }

  customElements.define('sn51-reviews', SomnilaReviews);
})();
